import React, { useState, useEffect } from 'react';
import { TextField, Button, Select, MenuItem, FormControl, InputLabel, Box, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TagIcon from '@mui/icons-material/Tag';

export default function TreeSearch({ onSearch, onItemNameSearch, initialId = '', initialType = 'item' }) {
  const [searchId, setSearchId] = useState(initialId);
  const [searchType, setSearchType] = useState(initialType);
  const [searchMode, setSearchMode] = useState('id'); // 'id' or 'item_name'
  const [itemNameQuery, setItemNameQuery] = useState('');

  // Update state when initial values change (e.g., from URL changes)
  useEffect(() => {
    setSearchId(initialId);
    setSearchType(initialType);
  }, [initialId, initialType]);

  const handleSearch = () => {
    if (searchMode === 'id' && searchId.trim()) {
      onSearch(searchId.trim(), searchType);
    } else if (searchMode === 'item_name' && itemNameQuery.trim() && onItemNameSearch) {
      onItemNameSearch(itemNameQuery.trim());
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const isSearchDisabled = searchMode === 'id' ? !searchId.trim() : !itemNameQuery.trim();

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        background: '#2d2d2d',
        padding: 2,
        borderRadius: 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        border: '1px solid #444',
      }}
    >
      <Tooltip title="Search by ID or by Item Name">
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={(e, newMode) => { if (newMode) setSearchMode(newMode); }}
          size="small"
        >
          <ToggleButton value="id">
            <Tooltip title="Search by ID"><TagIcon fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value="item_name">
            <Tooltip title="Search by Item Name"><SearchIcon fontSize="small" /></Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Tooltip>

      {searchMode === 'id' ? (
        <>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={searchType}
              label="Type"
              onChange={(e) => setSearchType(e.target.value)}
            >
              <MenuItem value="item">Item</MenuItem>
              <MenuItem value="vendor">Vendor</MenuItem>
              <MenuItem value="loot">Loot</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Enter ID"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., item_123"
            sx={{ width: 200 }}
          />
        </>
      ) : (
        <TextField
          size="small"
          label="Search by Item Name"
          value={itemNameQuery}
          onChange={(e) => setItemNameQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder='e.g., "Test - Scale - Item"'
          sx={{ width: 350 }}
        />
      )}

      <Button
        variant="contained"
        onClick={handleSearch}
        disabled={isSearchDisabled}
      >
        {searchMode === 'id' ? 'Load Tree' : 'Search'}
      </Button>
    </Box>
  );
}
